import type { Locale, MessageKey } from '@ecom/i18n';

import type { ActivityEvent } from './types';

import { STATUS_LABEL_KEY } from '@/features/orders/components/OrderStatusBadge';
import type { OrderStatus } from '@/features/orders/types';

/**
 * What the « Activité récente » list says about one event, as pure functions: the key to translate and its
 * parameters, so the wording is decided (and tested) apart from the rendering.
 *
 * Nothing here invents anything: every word comes from a field of a real journal row.
 */

/** The shop's own time zone (decision 2026-10-06): the same day boundaries as the analytics. */
export const SHOP_TIME_ZONE = 'Africa/Tunis';

const DATE_LOCALE: Readonly<Record<Locale, string>> = {
  fr: 'fr-TN',
  en: 'en-GB',
  ar: 'ar-TN',
};

export function formatActivityTime(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(DATE_LOCALE[locale], {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: SHOP_TIME_ZONE,
  }).format(new Date(iso));
}

const STOCK_TYPE_KEY: Readonly<Record<string, MessageKey>> = {
  manual_adjustment: 'merchant.activity.stock.manual_adjustment',
  confirmation: 'merchant.activity.stock.confirmation',
  release: 'merchant.activity.stock.release',
  shipment: 'merchant.activity.stock.shipment',
  return: 'merchant.activity.stock.return',
  loss: 'merchant.activity.stock.loss',
};

export interface ActivityLine {
  readonly key: MessageKey;
  readonly params: Readonly<Record<string, string>>;
}

/** « +10 » / « −3 » — the sign is always shown, so a movement is never read as a stock level. */
export function signedQuantity(delta: number): string {
  return delta > 0 ? `+${String(delta)}` : `−${String(Math.abs(delta))}`;
}

function isOrderStatus(value: string | null): value is OrderStatus {
  return value !== null && value in STATUS_LABEL_KEY;
}

/** The sentence for an event, or `null` for a journal row this version does not know how to word (it is then skipped, never guessed). */
export function describeActivity(
  event: ActivityEvent,
  translate: (key: MessageKey) => string,
): ActivityLine | null {
  if (event.kind === 'order_status') {
    if (event.orderNumber === null) return null;
    if (event.fromStatus === null && event.toStatus === 'ORDER_CREATED') {
      return {
        key: 'merchant.activity.order.created',
        params: { number: event.orderNumber },
      };
    }
    if (!isOrderStatus(event.toStatus)) return null;
    return {
      key: 'merchant.activity.order.changed',
      params: {
        number: event.orderNumber,
        to: translate(STATUS_LABEL_KEY[event.toStatus]),
      },
    };
  }

  const key = event.movementType === null ? undefined : STOCK_TYPE_KEY[event.movementType];
  if (key === undefined || event.quantityDelta === null) return null;
  const name = event.productName ?? '';
  const item = event.sku === null ? name : name === '' ? event.sku : `${name} (${event.sku})`;
  return {
    key,
    params: {
      item: item === '' ? translate('merchant.activity.stock.unknownItem') : item,
      delta: signedQuantity(event.quantityDelta),
      ...(event.orderNumber === null ? {} : { number: event.orderNumber }),
    },
  };
}
